import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { func, shape, arrayOf } from 'prop-types';
import { Row, Col, Typography, Button } from 'antd';
import { Field } from 'formik';
import AntSelect from '../commons/AntSelect';
import AntInput from '../commons/AntInput';
import AntRangePicker from '../commons/AntRangePicker';

import useFieldsLoader from './useFieldsLoader';
import withControlledOpen from './withControlledOpen';
import { baseExtraFieldsList } from '../../store/selectors/collections';
import ExtraColumn from './extraColumn';
import useDynamicFiltersLoader from './useDynamicFiltersLoader';
import useFieldsController from './useFieldsController';

const { Text } = Typography;

const controlledSelect = withControlledOpen(AntSelect);

const style = {
  container: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  item: {
    marginBottom: 0,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    width: 120,
    marginTop: 25,
    marginLeft: 10,
  },
};

const FiltersForm = ({
  values,
  directions,
  game,
  platform,
  eventTypes,
  onSubmit,
  setValue,
  extraFieldsList,
}) => {
  const [extraFields, setExtraFields] = useState(null);
  useFieldsLoader(values);
  useDynamicFiltersLoader(values, extraFields);
  useFieldsController(values, directions, setValue, extraFields);

  const resetFilters = () => {
    setValue('direction');
    setValue('game');
    setValue('platform');
    setValue('eventType');
    setValue('description');
    setValue('date');
    setValue('appVersion');
    setValue('audience');
    setValue('channel');
    setValue('techTag');
    setValue('classTag');
    setValue('typeTag');
    onSubmit({});
  };

  useEffect(() => {
    const { direction = [] } = values;
    const extraFields = direction
      .flatMap(
        item =>
          extraFieldsList.find(({ direction }) => direction === item)?.fields,
      )
      .filter(Boolean);
    setExtraFields(extraFields);
  }, [values, extraFieldsList]);

  return (
    <>
      <Row gutter={8}>
        <Col lg={3} sm={6}>
          <Text type="secondary">Direction</Text>
          <Field
            name="direction"
            placeholder="click and start typing..."
            component={controlledSelect}
            options={directions}
            itemStyle={style.item}
            mode="multiple"
            maxTagTextLength={5}
            maxTagCount={1}
            allowClear
          />
        </Col>
        <Col lg={3} sm={6}>
          <Text type="secondary">Event Type</Text>
          <Field
            name="eventType"
            data-test="eventType"
            placeholder="click and start typing..."
            component={controlledSelect}
            options={eventTypes}
            itemStyle={style.item}
            mode="multiple"
            maxTagTextLength={5}
            maxTagCount={0}
            allowClear
          />
        </Col>
        <Col lg={3} sm={6}>
          <Text type="secondary">Game</Text>
          <Field
            name="game"
            placeholder="click and start typing..."
            component={controlledSelect}
            options={game}
            itemStyle={style.item}
            mode="multiple"
            maxTagTextLength={5}
            maxTagCount={0}
            allowClear
          />
        </Col>
        <Col lg={3} sm={6}>
          <Text type="secondary">Platform</Text>
          <Field
            name="platform"
            placeholder="click and start typing..."
            component={controlledSelect}
            options={platform}
            itemStyle={style.item}
            mode="multiple"
            maxTagTextLength={5}
            maxTagCount={0}
            allowClear
          />
        </Col>
        <Col lg={3} sm={6}>
          <Text type="secondary">App Version</Text>
          <Field
            name="appVersion"
            placeholder="start typing..."
            component={AntInput}
            itemStyle={style.item}
          />
        </Col>
        <Col lg={3} sm={6}>
          <Text type="secondary">Description</Text>
          <Field
            name="description"
            placeholder="start typing..."
            component={AntInput}
            itemStyle={style.item}
          />
        </Col>
        <Col lg={6} sm={12}>
          <Text type="secondary">Date</Text>
          <Field
            name="date"
            component={AntRangePicker}
            itemStyle={style.item}
          />
        </Col>
      </Row>
      <Row gutter={8}>
        {extraFields &&
          extraFields.map(field => <ExtraColumn field={field} key={field} />)}

        <Col lg={4} style={style.buttonContainer}>
          <Button
            type="primary"
            style={style.button}
            onClick={() => onSubmit(values)}
            data-test="apply-filters"
          >
            Apply
          </Button>
          <Button type="secondary" style={style.button} onClick={resetFilters}>
            Reset
          </Button>
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = state => ({
  extraFieldsList: baseExtraFieldsList(state),
});

FiltersForm.propTypes = {
  values: shape({}).isRequired,
  directions: arrayOf(shape({})).isRequired,
  eventTypes: arrayOf(shape({})).isRequired,
  game: arrayOf(shape({})).isRequired,
  platform: arrayOf(shape({})).isRequired,
  extraFieldsList: arrayOf(shape({})).isRequired,
  onSubmit: func.isRequired,
  setValue: func.isRequired,
};

export default connect(mapStateToProps)(FiltersForm);
